import {MouseEventHandler, ReactNode, useEffect, useRef, useState} from 'react';
import useResizeObserver from 'use-resize-observer';
import {twMerge} from '../../../../common/twMerge';
import {LOG_CLICK_EVENT} from '../../../../logging/logEvent';
import {putTiaraObjectProps} from '../../../../logging/putTiaraObjectProps';
import {Icon} from '../../../atoms/Icon/Icon';
import {IconType} from '../../../atoms/Icon/IconType.enum';
import {SummaryType} from './SummaryType.enum';

export interface SummaryProps {
  expand: boolean;
  type: SummaryType;
  isMobile: boolean;
  onClickExpand: MouseEventHandler;
  children: ReactNode;
}

export function Summary({
  expand,
  type,
  isMobile,
  onClickExpand,
  children,
}: SummaryProps) {
  const contentRef = useRef<HTMLSpanElement | null>(null);
  const [showMoreButton, shouldShowMoreButton] = useState(false);
  const [containerSize, setContainerSize] = useState({width: 0, height: 0});

  const {ref: containerRef} = useResizeObserver<HTMLDivElement>({
    onResize: ({width, height}) => {
      if (!width || !height) {
        return;
      }
      setContainerSize({width, height});
    },
  });

  useEffect(() => {
    if (!contentRef.current) {
      return;
    }

    if (expand) {
      if (showMoreButton) {
        shouldShowMoreButton(false);
      }
      return;
    }

    const contentHeight = contentRef.current.scrollHeight;

    if (
      (type === SummaryType.Preview && contentHeight > 56) ||
      (type === SummaryType.About && contentHeight > 216)
    ) {
      shouldShowMoreButton(true);
    } else {
      shouldShowMoreButton(false);
    }
  }, [expand, showMoreButton, type, containerSize]);

  const mobileBlurColor = 'var(--colors-theme-solid-10)';
  const desktopBlurColor = 'var(--colors-theme-bg-surface-alt)';

  return (
    <div
      ref={containerRef}
      className={twMerge(
        'flex w-full flex-col items-center px-18pxr pb-10pxr',
        showMoreButton && 'cursor-pointer'
      )}
      onClick={e =>
        LOG_CLICK_EVENT(
          '줄거리더보기_클릭',
          e,
          showMoreButton ? onClickExpand : undefined
        )
      }
      {...putTiaraObjectProps({
        click: {layer1: '더보기'},
      })}
    >
      <div
        className={twMerge(
          'relative w-full',
          !expand && 'overflow-hidden',
          !expand && type === SummaryType.Preview && 'max-h-56pxr',
          !expand && type === SummaryType.About && 'max-h-[216px]'
        )}
      >
        <span
          ref={contentRef}
          className="font-small1 mb-8pxr block whitespace-pre-wrap break-words text-theme-solid-100"
        >
          {children}
        </span>
        {showMoreButton && (
          <div
            className="absolute top-0 left-0 h-full w-full"
            style={{
              background: isMobile
                ? `linear-gradient(180deg,
                    rgb(${mobileBlurColor} / 0%) 0%,
                    rgb(${mobileBlurColor} / 0%) 50%,
                    rgb(${mobileBlurColor} / 90%) 100%)`
                : `linear-gradient(180deg,
                    rgb(${desktopBlurColor} / 0%) 0%,
                    rgb(${desktopBlurColor} / 0%) 50%,
                    rgb(${desktopBlurColor} / 90%) 100%)`,
            }}
          />
        )}
      </div>
      {showMoreButton && (
        <div className="flex h-12pxr w-full items-center justify-center">
          <Icon type={IconType.ContentHomeArrowDown} />
        </div>
      )}
    </div>
  );
}
